import {
  coreExtensionData,
  createFrontendModule,
  createFrontendPlugin,
  PageBlueprint,
  PluginHeaderActionBlueprint,
} from '@backstage/frontend-plugin-api';
import { ButtonLink } from '@backstage/ui';
import { RiFolderSharedLine, RiPuzzleLine } from '@remixicon/react';
import { CatalogIcon } from '@xlrt/components';
import apiDocsPlugin from '@backstage/plugin-api-docs/alpha';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import {
  EntityCardBlueprint,
  EntityContentBlueprint,
  EntityContentLayoutBlueprint,
  EntityHeaderLayoutBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import { apiTypeOptions } from '@internal/plugin-catalog-model';
import { EntityPageFrame } from './entity-page/EntityPageFrame';
import { constants as myEntitiesConstants } from './my-entities/components/MyEntities/constants/catalog.constant';
import {
  catalogIndexRouteRef,
  createFromTemplateRouteRef,
  myEntitiesRouteRef,
  viewTechDocRouteRef,
} from './routes';

const MCP_SERVER_TYPE =
  apiTypeOptions.find(option => option.label === 'mcp-server')?.value ??
  'mcp-server';

/** AI resources that bundle other AI resources through `referencedAssets`. */

const catalogIndexLoader = () =>
  import('./components/Catalog/CatalogVer2').then(m => <m.CatalogVer2 />);

/* ------------------------------------------------------------------ */
/* Pages                                                               */
/* ------------------------------------------------------------------ */

/** My Assets: the entities the user or their groups own, at `/my-assets`. */
export const myEntitiesPage = PageBlueprint.make({
  name: 'my-entities',
  params: {
    path: '/my-assets',
    title: myEntitiesConstants.pageTitle,
    icon: <RiFolderSharedLine />,
    routeRef: myEntitiesRouteRef,
    loader: () =>
      import('./my-entities/components/MyEntities').then(m => <m.MyEntities />),
  },
});

/**
 * "New Entity" in the header of My Assets, the only page of this plugin: opens
 * the create hub.
 */
export const newEntityHeaderAction = PluginHeaderActionBlueprint.make({
  name: 'new-entity',
  params: {
    loader: async () => (
      <ButtonLink href="/create-hub" variant="secondary">
        {myEntitiesConstants.newEntityBtnLabel}
      </ButtonLink>
    ),
  },
});

/* ------------------------------------------------------------------ */
/* Entity page: header and overview layouts                            */
/* ------------------------------------------------------------------ */

/** The Xelerate header and tab bar of every entity page. */
export const entityHeaderLayout = EntityHeaderLayoutBlueprint.make({
  params: {
    loader: () =>
      import('./entity-page/EntityHeaderLayout').then(
        m => m.EntityHeaderLayout,
      ),
  },
});

/** Overview of users: warnings and the cards in two columns. */
export const userOverviewLayout = EntityContentLayoutBlueprint.make({
  name: 'user',
  params: {
    filter: { kind: 'user' },
    loader: () =>
      import('./entity-page/OverviewLayout').then(m => m.UserOverviewLayout),
  },
});

/** Overview of groups: the 9/3 grid without the top links row. */
export const groupOverviewLayout = EntityContentLayoutBlueprint.make({
  name: 'group',
  params: {
    filter: { kind: 'group' },
    loader: () =>
      import('./entity-page/OverviewLayout').then(
        m =>
          function GroupOverviewLayout(props) {
            return <m.OverviewLayout {...props} showTopLinks={false} />;
          },
      ),
  },
});

/** Overview of AI resources: the 9/3 grid, recording a resource view. */
export const aiResourceOverviewLayout = EntityContentLayoutBlueprint.make({
  name: 'airesource',
  params: {
    filter: { kind: 'airesource' },
    loader: () =>
      import('./entity-page/OverviewLayout').then(
        m => m.AiResourceOverviewLayout,
      ),
  },
});

/**
 * Overview of every other kind: top links and warnings, `content` cards in
 * the wide column, `info` cards in the narrow one.
 */
export const defaultOverviewLayout = EntityContentLayoutBlueprint.make({
  name: 'default',
  params: {
    filter: { $not: { kind: { $in: ['user', 'group', 'airesource'] } } },
    loader: () =>
      import('./entity-page/OverviewLayout').then(m => m.OverviewLayout),
  },
});

/* ------------------------------------------------------------------ */
/* Entity page: overview cards                                         */
/* ------------------------------------------------------------------ */

export const relationsGraphCard = EntityCardBlueprint.make({
  name: 'relations-graph',
  params: {
    type: 'content',
    filter: {
      $all: [
        { $not: { kind: { $in: ['api', 'group', 'user'] } } },
        { $not: { kind: 'airesource' } },
      ],
    },
    loader: () =>
      import('./components/RelationsGraph/RelationsGraph').then(m => (
        <m.RelationsGraph />
      )),
  },
});

/** The relations graph of APIs, limited to the kinds an API relates to. */
export const apiRelationsGraphCard = EntityCardBlueprint.make({
  name: 'api-relations-graph',
  params: {
    type: 'content',
    filter: { kind: 'api' },
    loader: () =>
      import('./components/RelationsGraph/RelationsGraph').then(m => (
        <m.RelationsGraph
          kinds={[
            'API',
            'Component',
            'Domain',
            'Group',
            'Location',
            'System',
            'User',
          ]}
        />
      )),
  },
});

export const subcomponentsCard = EntityCardBlueprint.make({
  name: 'subcomponents',
  params: {
    type: 'content',
    filter: {
      $not: {
        kind: {
          $in: ['api', 'group', 'user', 'system', 'domain', 'airesource'],
        },
      },
    },
    loader: () =>
      import('./components/Entities/Subcomponents').then(m => (
        <m.Subcomponents />
      )),
  },
});

export const systemEntitiesCard = EntityCardBlueprint.make({
  name: 'system-entities',
  params: {
    type: 'content',
    filter: { kind: 'system' },
    loader: () =>
      import('./components/Entities/SystemEntities').then(m => (
        <m.SystemEntities />
      )),
  },
});

export const systemsCard = EntityCardBlueprint.make({
  name: 'systems',
  params: {
    type: 'content',
    filter: { kind: 'domain' },
    loader: () =>
      import('./components/Entities/Systems').then(m => <m.Systems />),
  },
});

/** The definition of an API, except MCP servers. */
export const apiDefinitionCard = EntityCardBlueprint.make({
  name: 'api-definition',
  params: {
    type: 'content',
    filter: {
      $all: [{ kind: 'api' }, { $not: { 'spec.type': MCP_SERVER_TYPE } }],
    },
    loader: () =>
      import('./components/ApiDefinition/ApiDefinition').then(m => (
        <m.ApiDefinition />
      )),
  },
});

/** How to connect to an MCP server API. */
export const mcpConnectionDetailsCard = EntityCardBlueprint.make({
  name: 'mcp-connection-details',
  params: {
    type: 'content',
    filter: { kind: 'api', 'spec.type': MCP_SERVER_TYPE },
    loader: () =>
      import('./components/McpConnectionDetails/McpConnectionDetails').then(
        m => <m.McpConnectionDetails />,
      ),
  },
});

export const aiResourceDefinitionCard = EntityCardBlueprint.make({
  name: 'airesource-definition',
  params: {
    type: 'content',
    filter: {
      $all: [
        { kind: 'airesource' },
        { $not: { 'spec.type': { $in: ['bundle'] } } },
      ],
    },
    loader: () =>
      import('./components/AiResourceDefinition/AiResourceDefinition').then(
        m => <m.AiResourceDefinition />,
      ),
  },
});

/** The AI resources a bundle includes, grouped by type. */
export const aiResourceBundleCard = EntityCardBlueprint.make({
  name: 'airesource-bundle',
  params: {
    type: 'content',
    filter: { kind: 'airesource', 'spec.type': 'bundle' },
    loader: () =>
      import('./components/AiResourceBundle/AiResourceBundle').then(m => (
        <m.AiResourceBundle />
      )),
  },
});

export const groupMembersCard = EntityCardBlueprint.make({
  name: 'group-members',
  params: {
    type: 'content',
    filter: { kind: 'group' },
    loader: () =>
      import('./components/GroupMembers/GroupMembers').then(m => (
        <m.GroupMembers />
      )),
  },
});

export const userProfileCard = EntityCardBlueprint.make({
  name: 'user-profile',
  params: {
    type: 'content',
    filter: { kind: 'user' },
    loader: () =>
      import('./components/UserProfileCard/UserProfileCard').then(m => (
        <m.UserProfileCard />
      )),
  },
});

/** Owner, description, tags, links and relations, in the narrow column. */
export const overviewSidebarCard = EntityCardBlueprint.make({
  name: 'overview-sidebar',
  params: {
    type: 'info',
    filter: { $not: { kind: 'user' } },
    loader: () =>
      import('./components/OverviewSidebar/OverviewSidebar').then(m => (
        <m.OverviewSidebar />
      )),
  },
});

/* ------------------------------------------------------------------ */
/* Entity page: tabs                                                   */
/* ------------------------------------------------------------------ */

export const dependenciesEntityContent = EntityContentBlueprint.make({
  name: 'dependencies',
  params: {
    path: '/dependencies',
    title: 'Dependencies',
    filter: {
      kind: 'component',
      'spec.type': { $in: ['service', 'website'] },
    },
    loader: () =>
      import('./entity-content/DependenciesTab').then(m => (
        <m.DependenciesTab />
      )),
  },
});

export const diagramEntityContent = EntityContentBlueprint.make({
  name: 'diagram',
  params: {
    path: '/diagram',
    title: 'Diagram',
    filter: { kind: 'system' },
    loader: () =>
      import('./entity-content/DiagramTab').then(m => <m.DiagramTab />),
  },
});

/**
 * The APIs tab of system entities: every API the system provides or has as a
 * part, each with its definition, behind the limited-access permission.
 */
export const apiExplorerEntityContent = EntityContentBlueprint.make({
  name: 'api-explorer',
  params: {
    path: '/api-explorer',
    title: 'APIs',
    group: 'development',
    filter: { kind: 'system' },
    loader: () =>
      import('./entity-content/ApiExplorerTab').then(m => <m.ApiExplorerTab />),
  },
});

/** The Updates tab of APIs: the release notes of the API. */
export const apiUpdatesEntityContent = EntityContentBlueprint.make({
  name: 'api-updates',
  params: {
    path: '/updates',
    title: 'Updates',
    filter: { kind: 'api' },
    loader: () =>
      import('./components/ApiReleaseNotes/ApiReleaseNotes').then(m => (
        <m.ApiReleaseNotes />
      )),
  },
});

/** The README tab of bundle AI resources, from the `readme-location` annotation. */
export const aiResourceReadmeEntityContent = EntityContentBlueprint.make({
  name: 'airesource-readme',
  params: {
    path: '/readme',
    title: 'README',
    filter: { kind: 'airesource' },
    loader: () =>
      import('./entity-content/AiResourceMarkdownTab').then(m => (
        <m.AiResourceMarkdownTab annotation="readme-location" />
      )),
  },
});

/** The ChangeLog tab of bundle AI resources, from the `changelog-location` annotation. */
export const aiResourceChangelogEntityContent = EntityContentBlueprint.make({
  name: 'airesource-changelog',
  params: {
    path: '/changelog',
    title: 'ChangeLog',
    filter: { kind: 'airesource' },
    loader: () =>
      import('./entity-content/AiResourceMarkdownTab').then(m => (
        <m.AiResourceMarkdownTab annotation="changelog-location" />
      )),
  },
});

/** The Relations tab of bundle AI resources, a placeholder for now. */
export const aiResourceRelationsEntityContent = EntityContentBlueprint.make({
  name: 'airesource-relations',
  params: {
    path: '/relations',
    title: 'Relations',
    filter: { kind: 'airesource' },
    loader: () =>
      import('./components/RelationsGraph/RelationsGraph').then(m => (
        <m.RelationsGraph />
      )),
  },
});

/* ------------------------------------------------------------------ */
/* Plugin and core overrides                                           */
/* ------------------------------------------------------------------ */

/**
 * The Xelerate catalog: My Assets, and the header, layouts, cards and tabs of
 * the entity page. The core catalog and API docs plugins keep the catalog
 * index, the API explorer and the entity page; install
 * `catalogOverridesModule` and `apiExplorerModule` next to this plugin.
 */
export const xelerateCatalogPlugin = createFrontendPlugin({
  pluginId: 'xelerate-catalog',
  extensions: [
    myEntitiesPage,
    newEntityHeaderAction,
    entityHeaderLayout,
    userOverviewLayout,
    groupOverviewLayout,
    aiResourceOverviewLayout,
    defaultOverviewLayout,
    relationsGraphCard,
    apiRelationsGraphCard,
    subcomponentsCard,
    systemEntitiesCard,
    systemsCard,
    apiDefinitionCard,
    mcpConnectionDetailsCard,
    aiResourceDefinitionCard,
    aiResourceBundleCard,
    groupMembersCard,
    userProfileCard,
    overviewSidebarCard,
    dependenciesEntityContent,
    diagramEntityContent,
    apiExplorerEntityContent,
    apiUpdatesEntityContent,
    aiResourceReadmeEntityContent,
    aiResourceChangelogEntityContent,
    aiResourceRelationsEntityContent,
  ],
  routes: {
    myEntities: myEntitiesRouteRef,
  },
  externalRoutes: {
    viewTechDoc: viewTechDocRouteRef,
    createFromTemplate: createFromTemplateRouteRef,
    catalogIndex: catalogIndexRouteRef,
  },
});

/** Replaces the default catalog index page at `/catalog`. */
export const catalogIndexPageOverride = catalogPlugin
  .getExtension('page:catalog')
  .override({ params: { icon: <CatalogIcon />, loader: catalogIndexLoader } });

/**
 * The core entity page without the plugin header (the Xelerate header layout
 * renders the title, metadata and tabs), inside a scroll container.
 */
export const catalogEntityPageOverride = catalogPlugin
  .getExtension('page:catalog/entity')
  .override({
    factory(originalFactory) {
      const page = originalFactory({ params: { noHeader: true } });
      return [
        ...page,
        coreExtensionData.reactElement(
          <EntityPageFrame>
            {page.get(coreExtensionData.reactElement)}
          </EntityPageFrame>,
        ),
      ];
    },
  });

/** Uses the Xelerate search card, which renders Quality Seal status icons. */
export const catalogSearchResultListItemOverride = catalogPlugin
  .getExtension('search-result-list-item:catalog')
  .override({ disabled: true });

/** Overrides of the core catalog plugin, installed by package discovery. */
export const catalogOverridesModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    catalogIndexPageOverride,
    catalogEntityPageOverride,
    catalogSearchResultListItemOverride,
  ],
});

/**
 * Replaces the core API explorer with the catalog index filtered to APIs, and
 * moves it from `/api-docs` to `/apis`. The route ref of the API docs plugin
 * stays, so links to its root land on `/apis`.
 */
export const apiExplorerPageOverride = apiDocsPlugin
  .getExtension('page:api-docs')
  .override({
    params: {
      path: '/apis',
      icon: <RiPuzzleLine />,
      loader: catalogIndexLoader,
    },
  });

/** Overrides of the core API docs plugin, installed by package discovery. */
export const apiExplorerModule = createFrontendModule({
  pluginId: 'api-docs',
  extensions: [apiExplorerPageOverride],
});
